/**
 * Phase 7 Slice D.2 — Cockpit WebSocket hook.
 *
 * Opens a single per-tenant WebSocket to `/api/v1/ws/cockpit` and dispatches
 * inbound envelopes to the caller via a stable handler. Token expiry close
 * code (4401) triggers a one-shot retry that hits the JWT refresh path
 * before re-opening; subsequent close codes back off exponentially.
 *
 * Usage:
 *   const status = useTenantWebSocket(({ type, payload }) => {
 *     if (type === "cockpit.invalidate") qc.invalidateQueries(...);
 *   });
 *
 * The hook accepts an optional `enabled` flag (default true) so pages that
 * shouldn't subscribe (e.g. login screen) can opt out without unmounting.
 */
import { useEffect, useRef, useState } from "react";
import { auth, API_BASE } from "./api";
import axios from "axios";

function wsUrlForToken(token) {
  // API_BASE is something like "https://<host>/api/v1". Swap protocol to ws/wss.
  const base = API_BASE.replace(/^http/i, "ws");
  return `${base}/ws/cockpit?token=${encodeURIComponent(token)}`;
}

async function refreshAccessToken() {
  const rt = auth.getRefresh();
  if (!rt) return null;
  try {
    const r = await axios.post(`${API_BASE}/auth/refresh`,
      { refresh_token: rt });
    auth.setSession({ access_token: r.data.access_token,
                          refresh_token: r.data.refresh_token });
    return r.data.access_token;
  } catch (e) {
    return null;
  }
}

export function useTenantWebSocket(onMessage, { enabled = true } = {}) {
  const [status, setStatus] = useState("idle");  // idle|connecting|open|closed|error
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const wsRef = useRef(null);
  const retryRef = useRef({ attempt: 0, timer: null });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const connect = async () => {
      const token = auth.getAccess();
      if (!token) {
        setStatus("idle");
        return;
      }
      setStatus("connecting");
      try {
        const ws = new WebSocket(wsUrlForToken(token));
        wsRef.current = ws;

        ws.onopen = () => {
          if (cancelled) { ws.close(); return; }
          setStatus("open");
          retryRef.current.attempt = 0;
        };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            onMessageRef.current && onMessageRef.current(msg);
          } catch (e) { /* ignore malformed */ }
        };
        ws.onerror = () => { setStatus("error"); };
        ws.onclose = async (ev) => {
          setStatus("closed");
          if (cancelled) return;
          // 4401 = token rejected/expired. Try one JWT refresh + immediate reopen.
          if (ev.code === 4401 && retryRef.current.attempt === 0) {
            retryRef.current.attempt = 1;
            const fresh = await refreshAccessToken();
            if (fresh && !cancelled) { connect(); return; }
          }
          // Otherwise back off exponentially up to ~30s.
          const a = ++retryRef.current.attempt;
          const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(a, 5)));
          retryRef.current.timer = setTimeout(() => {
            if (!cancelled) connect();
          }, delay);
        };
      } catch (e) {
        setStatus("error");
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (retryRef.current.timer) clearTimeout(retryRef.current.timer);
      retryRef.current.timer = null;
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws && (ws.readyState === WebSocket.OPEN
                  || ws.readyState === WebSocket.CONNECTING)) {
        try { ws.close(1000, "unmount"); } catch (e) { /* ignore */ }
      }
    };
  }, [enabled]);

  return status;
}
